/*
 * Progressive atmosphere for collection.wrapping-pappers only.
 * The asset changes presentation and leaves products, copy, links and section
 * settings under Shopify theme-editor control.
 */

(() => {
  const main = document.getElementById('main-content');
  const hero = main?.querySelector('[id$="__image_banner_xWLUEm"]');

  // A second guard prevents the enhancement running on another collection.
  if (!main || !hero) return;

  main.classList.add('paper-studio-enhanced');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sectionSuffixes = [
    '__rich_text_TDC4id',
    '__promo_grid_tU3ck9',
    '__promo_strip_xEjU3U',
    '__collection-products',
    '__promo_strip_CmyDmn',
    '__image_banner_cjzTzY',
    '__blocks_9r3Wry',
    '__rich_text_NWpMmx',
    '__multi_column_J3kcxq',
    '__faq_NT4mCb',
    '__1784726003f090f483'
  ];

  const sections = sectionSuffixes
    .map((suffix) => main.querySelector(`[id$="${suffix}"]`))
    .filter(Boolean);

  // The mobile product grid can be many viewports tall, so it is deliberately
  // never made transparent as one observed block.
  const revealSections = sections.filter(
    (section) => !section.matches('[id$="__collection-products"]')
  );

  const revealNodes = (nodes, hiddenClass, visibleClass, threshold = .015) => {
    if (reducedMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add(visibleClass));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(visibleClass);
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -5% 0px',
      threshold
    });

    nodes.forEach((node) => {
      node.classList.add(hiddenClass);
      observer.observe(node);
    });
  };

  revealNodes(revealSections, 'paper-studio-reveal', 'is-paper-studio-visible');

  const sampleCards = Array.from(
    main.querySelectorAll('[id$="__promo_grid_tU3ck9"] .gallery-block')
  );
  // The native promo-grid removes later cards from the tab order. On this
  // collection each sample is a primary navigation link, so keep all seven
  // available to keyboard users.
  sampleCards.forEach((card) => card.removeAttribute('tabindex'));

  const guideCards = Array.from(
    main.querySelectorAll('[id$="__multi_column_J3kcxq"] .multi-column')
  );

  revealNodes(
    [...sampleCards, ...guideCards],
    'paper-studio-item-reveal',
    'is-paper-studio-item-visible',
    .01
  );

  const makeLayer = (className, parent) => {
    const layer = document.createElement('div');
    layer.className = className;
    layer.setAttribute('aria-hidden', 'true');
    parent.appendChild(layer);
    return layer;
  };

  const route = makeLayer('paper-studio-route', main);
  route.innerHTML = `
    <svg viewBox="0 0 100 1000" preserveAspectRatio="none" aria-hidden="true">
      <path class="paper-studio-route__line" d="M4 -20 C18 45 2 105 10 165 S18 285 5 355 S2 490 11 565 S17 700 5 770 S2 905 10 1020" />
      <path class="paper-studio-route__line paper-studio-route__line--blush" d="M96 -20 C82 55 99 120 90 190 S82 320 96 390 S99 520 89 600 S83 735 96 815 S98 935 90 1020" />
    </svg>`;

  const motifTypes = ['scissors', 'ribbon', 'tag', 'pencil'];
  const decoratedSections = [
    main.querySelector('[id$="__rich_text_TDC4id"]'),
    main.querySelector('[id$="__promo_grid_tU3ck9"]'),
    main.querySelector('[id$="__collection-products"]'),
    main.querySelector('[id$="__image_banner_cjzTzY"]'),
    main.querySelector('[id$="__rich_text_NWpMmx"]'),
    main.querySelector('[id$="__multi_column_J3kcxq"]'),
    main.querySelector('[id$="__faq_NT4mCb"]')
  ].filter(Boolean);

  decoratedSections.forEach((section, sectionIndex) => {
    const field = makeLayer('paper-studio-motif-field', section);
    const count = section.matches('[id$="__collection-products"]') ? 8 : 4;

    for (let index = 0; index < count; index += 1) {
      const motif = document.createElement('span');
      const type = motifTypes[(sectionIndex + index) % motifTypes.length];
      const onLeft = index % 2 === 0;
      const x = onLeft
        ? 1 + ((sectionIndex * 3 + index * 2) % 9)
        : 89 + ((sectionIndex * 2 + index) % 8);
      const y = 8 + ((sectionIndex * 19 + index * 29) % 84);

      motif.className = `paper-studio-motif paper-studio-motif--${type}`;
      motif.style.setProperty('--motif-x', `${x}%`);
      motif.style.setProperty('--motif-y', `${y}%`);
      motif.style.setProperty('--motif-size', `clamp(${66 + (index % 3) * 9}px, ${5.8 + (index % 3) * .75}vw, ${112 + (index % 3) * 14}px)`);
      const rotation = -19 + ((sectionIndex * 13 + index * 23) % 39);
      motif.style.setProperty('--motif-rotation', `${rotation}deg`);
      motif.style.setProperty('--motif-rotation-mid', `${rotation + 4.5}deg`);
      motif.style.setProperty('--motif-rotation-end', `${rotation - 2.5}deg`);
      motif.style.setProperty('--motif-drift-x', `${9 + (index % 3) * 5}px`);
      motif.style.setProperty('--motif-drift-y', `${11 + (sectionIndex % 3) * 5}px`);
      motif.style.setProperty('--motif-drift-up', `${-5 - (sectionIndex % 3) * 3}px`);
      motif.style.setProperty('--motif-return-x', `${-5 - (index % 3) * 3}px`);
      motif.style.setProperty('--motif-duration', `${10 + ((sectionIndex + index) % 5) * 2.1}s`);
      motif.style.setProperty('--motif-delay', `${-2 - sectionIndex * 1.4 - index * 1.9}s`);
      motif.style.setProperty('--motif-opacity', `${.31 + (index % 3) * .05}`);
      field.appendChild(motif);
    }
  });

  const rolledSection = main.querySelector('[id$="__promo_strip_CmyDmn"]');
  if (rolledSection) {
    const roll = makeLayer('paper-studio-roll', rolledSection);
    roll.innerHTML = '<span class="paper-studio-roll__photo"></span>';
  }

  let frameRequested = false;

  const updateHeroDrift = () => {
    frameRequested = false;
    const heroBounds = hero.getBoundingClientRect();
    const visible = heroBounds.bottom > 0 && heroBounds.top < window.innerHeight;

    if (!visible || window.innerWidth < 769 || reducedMotion) return;

    const progress = Math.max(0, Math.min(1, -heroBounds.top / Math.max(heroBounds.height, 1)));
    hero.style.setProperty('--paper-hero-shift', `${progress * 22}px`);
  };

  const requestHeroDrift = () => {
    if (frameRequested || document.hidden) return;
    frameRequested = true;
    window.requestAnimationFrame(updateHeroDrift);
  };

  updateHeroDrift();
  window.addEventListener('scroll', requestHeroDrift, { passive: true });
  window.addEventListener('resize', requestHeroDrift, { passive: true });
})();
